import React, { useEffect, useRef, useState, memo } from 'react';

const LazyImage = memo(({ 
  src, 
  alt, 
  className = '', 
  placeholder = 'bg-gray-300 animate-pulse',
  onLoad,
  webpSrc, // Optional WebP version
  ...props 
}) => {
  const imgRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(img);
        }
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.1
      }
    );

    observer.observe(img);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView && !imageSrc && !hasError) {
      // Try to load WebP first if available
      const testSrc = webpSrc || src;
      
      const img = new Image();
      img.onload = () => {
        setImageSrc(testSrc);
        setIsLoaded(true);
        if (onLoad) onLoad();
      };
      img.onerror = () => {
        if (webpSrc && testSrc === webpSrc) {
          // WebP failed, try original format
          const fallbackImg = new Image();
          fallbackImg.onload = () => {
            setImageSrc(src);
            setIsLoaded(true);
            if (onLoad) onLoad();
          };
          fallbackImg.onerror = () => {
            console.error(`Failed to load image: ${src}`);
            setHasError(true);
          };
          fallbackImg.src = src;
        } else {
          console.error(`Failed to load image: ${src}`);
          setHasError(true);
        }
      };
      img.src = testSrc;
    }
  }, [isInView, src, webpSrc, imageSrc, hasError, onLoad]);

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${className}`} {...props}>
      {!isLoaded && !hasError && (
        <div className={`absolute inset-0 ${placeholder}`} />
      )}
      {hasError && (
        <div className="absolute inset-0 bg-gray-400 flex items-center justify-center">
          <span className="text-gray-600 text-sm">Failed to load</span>
        </div>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${className}`}
          onLoad={() => setIsLoaded(true)}
        />
      )}
    </div>
  );
});

export default LazyImage;